import { Constants } from '../../../../../../constants/redeProto';
import { RegionChart } from '../../components/charts/RegionChart';
import { TrendChart } from '../../components/charts/TrendChart';
import { KpiCard } from '../../components/KpiCard';
import { PillarCard } from '../../components/PillarCard';
import { SectionCard } from '../../components/SectionCard';
import { StateFeedback } from '../../components/StateFeedback';
import { StatesTable } from '../../components/StatesTable';
import {
  classificationColor,
  csatColor,
  heatColor,
  protoColors,
} from '../../theme/tokens';
import { formatCsat, formatInt, formatPct } from '../../utils/format';
import { ChartsGrid, KpiGrid, PillarGrid, SectionSpacer } from './styles';
import useOverview from './useOverview';

export function Overview(): JSX.Element {
  const {
    overview,
    pillars,
    states,
    evolution,
    regionDistribution,
    loading,
    error,
  } = useOverview();

  const trendLabels = evolution.map((point) => point.month);
  const slaSeries = evolution.map((point) => point.slaPct);
  const approvalSeries = evolution.map((point) => point.approvalPct);

  return (
    <StateFeedback loading={loading} error={error} empty={!overview}>
      {overview && (
        <>
          <KpiGrid>
            <KpiCard
              label={Constants.kpis.activeAccredited}
              value={formatInt(overview.totalAccredited)}
              hint={`${formatInt(overview.totalPoles)} polos · ${formatInt(
                overview.totalStates
              )} UFs`}
            />
            <KpiCard
              label={Constants.kpis.goal}
              value={formatInt(overview.totalGoal)}
              hint="Meta total de credenciados"
            />
            <KpiCard
              label={Constants.kpis.coverage}
              value={formatPct(overview.overallAchievement)}
              hint={`${formatInt(overview.coveredCities)} municípios cobertos`}
              accentColor={heatColor(overview.overallAchievement)}
            />
            <KpiCard
              label={Constants.kpis.criticalPoles}
              value={formatInt(overview.criticalPoles)}
              hint="Exigem ação imediata"
              accentColor={classificationColor.critical}
            />
            <KpiCard
              label={Constants.kpis.sla}
              value={formatPct(overview.networkSla)}
              hint="Média dos polos"
              accentColor={heatColor(overview.networkSla)}
            />
            <KpiCard
              label={Constants.kpis.csat}
              value={formatCsat(overview.avgCsat)}
              hint="Escala 0–5"
              accentColor={csatColor(overview.avgCsat)}
            />
            <KpiCard
              label={Constants.kpis.firstReviewApproval}
              value={formatPct(overview.firstReviewApproval)}
              hint="Média dos polos"
              accentColor={heatColor(overview.firstReviewApproval)}
            />
            <KpiCard
              label={Constants.kpis.regionsBelowGoal}
              value={formatInt(overview.regionsBelowGoal)}
              hint="De 5 regiões"
              accentColor={
                overview.regionsBelowGoal > 0
                  ? classificationColor.attention
                  : classificationColor.healthy
              }
            />
          </KpiGrid>

          <SectionCard
            title="Os Quatro Pilares"
            subtitle="Balanced Scorecard da rede — detalhes na aba BSC"
          >
            <PillarGrid>
              {pillars.map((pillar) => (
                <PillarCard key={pillar.key} pillar={pillar} compact />
              ))}
            </PillarGrid>
          </SectionCard>

          <ChartsGrid>
            <SectionCard
              title="Evolução · SLA e Aprovação 1ª revisão"
              subtitle="Últimos 8 meses (%)"
            >
              <TrendChart
                labels={trendLabels}
                series={[
                  {
                    label: 'SLA',
                    color: protoColors.purple,
                    data: slaSeries,
                  },
                  {
                    label: 'Aprovação 1ª revisão',
                    color: protoColors.success,
                    data: approvalSeries,
                  },
                ]}
              />
            </SectionCard>
            <SectionCard
              title="Distribuição por Região"
              subtitle="Credenciados atuais vs meta"
            >
              <RegionChart data={regionDistribution} />
            </SectionCard>
          </ChartsGrid>

          <SectionSpacer>
            <SectionCard
              title="Cobertura por UF"
              subtitle={`${states.length} estados · atingimento da meta`}
            >
              <StatesTable states={states} />
            </SectionCard>
          </SectionSpacer>
        </>
      )}
    </StateFeedback>
  );
}
